/* eslint-disable no-unused-vars, no-useless-constructor*/
import React from 'react';
import Select, {OptGroup, Option} from 'rc-select';
import PropTypes from 'prop-types';
import {Row, Col} from 'react-bootstrap';
import {Fade} from 'react-reveal';
import _ from 'lodash';

import MicrositeSection from '../MicrositePage/MicrositeSection/MicrositeSection';
import BaseComponent from "../../../panther/components/base/BaseComponent";

import {ColumnChart, HoverHandler} from 'gisat-panther-charts';
import LeafletMap from "../../../panther/maps/LeafletMap/presentation";
import ComparatorContainer from "./ComparatorContainer";

// images
import scenarioRef from '../../../resources/img/ref.png';
import scenario1 from '../../../resources/img/scenario-1.png';
import scenario2 from '../../../resources/img/scenario-2.png';
import scenario3 from '../../../resources/img/scenario-3.png';
import scenario4 from '../../../resources/img/scenario-4.png';

// attribute data
import aoi from './data/level1/aoi.json';

import park from './data/level2/park.json';
import center from './data/level2/center.json';
import eastPart from './data/level2/east-part.json';
import northPart from './data/level2/north-part.json';
import westPart from './data/level2/west-part.json';

import square from './data/level3/square.json';
import busStop1 from './data/level3/busStop1.json';
import busStop2 from './data/level3/busStop2.json';
import parking from './data/level3/parking.json';
import marianColumn from './data/level3/marianColumn.json';

// spatial data
import aoiGeometry from './geometries/al_1';
import zoneGeometries from './geometries/al_2';
import sectorGeometries from './geometries/al_3';

import './style.css';

const scenarioComparatorOptions = [
    {
        value: "scenarioRef",
        image: scenarioRef,
        label: "Reference",
    }, {
        value: "scenario1",
        image: scenario1,
        label: "Scenario 1",
    }, {
        value: "scenario2",
        image: scenario2,
        label: "Scenario 2",
    }, {
        value: "scenario3",
        image: scenario3,
        label: "Scenario 3",
    }, {
        value: "scenario4",
        image: scenario4,
        label: "Scenario 4",
    }
];

// const backgroundMap = {
//     key: 'background-osm',
//     type: 'wmts',
//     options: {
//         url: 'http://{s}.tile.osm.org/{z}/{x}/{y}.png',
//         attribution: 'Map data &copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors, <a href="https://creativecommons.org/licenses/by-sa/2.0/">CC-BY-SA</a>'
//     }
// };

const backgroundMap = {
    key: 'cuzk_ortofoto',
    name: 'CUZK Ortofoto',
    type: 'wms',
    options: {
        url: 'http://geoportal.cuzk.cz/WMS_ORTOFOTO_PUB/WMService.aspx?',
        params: {
            layers: 'GR_ORTFOTORGB'
        },
        attribution: "Background map: <a target='_blank' href='https://cuzk.cz'>@ČÚZK</a>"
    }
};

const fidColumn = "FID";
const nameColumn = "NAME_EN";

const level1 = {
    key: 'level1',
    type: 'vector',
    options: {
        features: aoiGeometry.features,
        keyProperty: fidColumn
    }
};

const level2 = {
    key: 'level2',
    type: 'vector',
    options: {
        features: zoneGeometries.features,
        keyProperty: fidColumn
    }
};

const level3 = {
    key: 'level3',
    type: 'vector',
    options: {
        features: sectorGeometries.features,
        keyProperty: fidColumn
    }
};

const options = [
    {
        value: "level-1",
        layers: [level1],
        attributes: {
            1: aoi
        },
        label: "Level 1 - Hodonín AOI",
    }, {
        value: "level-2",
        layers: [level2],
        label: "Level 2 - Zones",
        attributes: {
            1: westPart,
            2: northPart,
            3: center,
            4: eastPart,
            5: park
        },
    }, {
        value: "level-3",
        layers: [level3],
        label: "Level 3 - Sectors",
        attributes: {
            1: square,
            2: busStop1,
            3: busStop2,
            4: parking,
            5: marianColumn
        },
    }
];

const defaultSelectedLevel = options[1];
const defaultSelectedFeature = 1;

class Service2 extends BaseComponent {
    constructor(props) {
        super(props);

        this.state = {
            selectedLevel: defaultSelectedLevel,
            selectedFeature: defaultSelectedFeature,
            topScenario: scenarioComparatorOptions[0],
            bottomScenario: scenarioComparatorOptions[1],
            mapView: {
                center: {lat: 48.8491, lon: 17.1286},
                boxRange: 500
            }
        };

        this.onLevelChange = this.onLevelChange.bind(this);
        this.onFeatureClick = this.onFeatureClick.bind(this);
        this.onBottomScenarioChange = this.onBottomScenarioChange.bind(this);
        this.onTopScenarioChange = this.onTopScenarioChange.bind(this);
    }

    render() {
        let content = this.renderContent();

        return (
            <MicrositeSection
                id={this.props.id}
                name={this.props.name}
                textCentered={this.props.textCentered}
                content={content}
            />
        );
    }

    onTopScenarioChange(label) {
        let topScenario = _.find(scenarioComparatorOptions, {label});
        this.setState({
            topScenario
        });
    }

    onBottomScenarioChange(label) {
        let bottomScenario = _.find(scenarioComparatorOptions, {label});
        this.setState({
            bottomScenario
        });
    }

    onLevelChange(label, option) {
        let selectedLevel = _.find(options, {label});
        this.setState({
            selectedLevel,
            selectedFeature: defaultSelectedFeature
        });
    }

    onFeatureClick(key) {
        if (key) {
            this.setState({
                selectedFeature: key
            })
        }
    }

    renderContent() {
        let selectedLayerFeatures = this.state.selectedLevel.layers[0].options.features;
        let feature = _.find(selectedLayerFeatures, (feature) => {
            return feature.properties[fidColumn] === this.state.selectedFeature;
        });
        let selectedFeatureName = feature.properties[nameColumn];

        return (
            <div>
                <Row className="show-grid">
                    <Fade>
                        <Col xs={12} sm={10} smOffset={1} md={10} mdOffset={1} lg={8} lgOffset={2}>
                            <p className="centered">This model allows you to assess the impact of your urban planning decisions at a very local level (in 1m spatial resolution) on the local heat stress conditions and thermal comfort of people present on that place.</p>
                            <p className="centered">As a case study, we present a Hodonin´s town square example. While planning the re-design of the square, one of the aspects the Hodonin´s municipality took into consideration was the impact of the planned square design on local heat stress conditions.</p>
                            <p className="centered">To assess this impact, we provided a heat stress modelling, based on a widely used indicator - WBGT Index (Web-bulb Globe Temperature, ISO 7243:2017). This index expresses the level of heat stress pressure on human. The higher the heat stress level, the lower the human thermal comfort. Comparing to other similar indicators, WBGT takes into consideration not only the air temperature, but also the radial heat, air humidity and wind flux and thus better quantifies human thermal comfort.  The simulation is run by Urban Climate Model UrbClim, developed by Belgian research institute VITO.</p>
                            <p className="centered">First, the current status of the heat stress level and distribution on the square has been modelled. Second, few potential scenarios of the re-design of the square has been simulated, as proposed by Hodonin´s municipality. Following features represent the input parameters for the heat stress modelling: distribution and heights of buildings, distribution of grass patches, distribution of trees and even sizes of tree crowns. Based on this, the model simulates also the coverage of the shadows, exposition to solar radiance during the day and wind flux.</p>
                            <p className="centered">The results of simulation of daily mean WGBT index (for the warmest day of 2016) at the Hodonin square for current situation and each of proposed scenarios are presented below.</p>
                        </Col>
                    </Fade>
                </Row>

                <Row className="show-grid">
                    <Fade left distance="30px" delay={500}>
                        <Col className="climate-fit-scenario-set-comparator" xs={12} sm={12} md={12} lg={10} lgOffset={1}>
                            <div className="climate-fit-level-select">
                                <Select
                                    value={this.state.topScenario.label}
                                    onChange={this.onTopScenarioChange}
                                >
                                    {this.renderScenariosOptions()}
                                </Select>
                            </div>
                            <ComparatorContainer
                                sliderLineColor="#374c5f"
                                vertical
                                maxWidth={800}
                                leftImageSrc={this.state.topScenario.image}
                                rightImageSrc={this.state.bottomScenario.image}
                            />
                            <div className="climate-fit-level-select">
                                <Select
                                    value={this.state.bottomScenario.label}
                                    onChange={this.onBottomScenarioChange}
                                >
                                    {this.renderScenariosOptions()}
                                </Select>
                            </div>
                        </Col>
                    </Fade>
                </Row>

                <Row className="show-grid equal-height climate-fit-visualization">
                    <Col className="climate-fit-map-wrap" xs={12} sm={12} md={7} lg={8}>
                        <div className="climate-fit-map-container">
                            <HoverHandler>
                                <LeafletMap
                                    mapKey='map'
                                    view={this.state.mapView}
                                    backgroundLayer={backgroundMap}
                                    layers={this.state.selectedLevel.layers}

                                    scale
                                    zoomControl
                                    onFeatureClick={this.onFeatureClick}
                                    selectedFeature={this.state.selectedFeature}
                                />
                            </HoverHandler>
                        </div>
                    </Col>
                    <Col className="ptr-light climate-fit-chart-wrap" xs={12} sm={12} md={5} lg={4}>
                        <div className="climate-fit-level-select">
                            <Select
                                value={this.state.selectedLevel.label}
                                onChange={this.onLevelChange}
                            >
                                {this.renderOptions()}
                            </Select>
                        </div>
                        <div className="climate-fit-chart">
                            <div className="climate-fit-chart-header">{selectedFeatureName}</div>
                            <HoverHandler>
                                <ColumnChart
                                    maxWidth={20}
                                    height={17}

                                    key="typical-example"
                                    data={this.state.selectedLevel.attributes[this.state.selectedFeature]}
                                    keySourcePath="key"
                                    nameSourcePath="data.name"
                                    xSourcePath="data.name"
                                    ySourcePath="data.value"
                                    defaultColor="#ef7855"
                                    highlightColor="#192128"

                                    sorting={[["data.name", "asc"]]}
                                    xValuesSize={4}
                                    yValuesSize={2}
                                    yLabel
                                    yOptions={{
                                        max: 6,
                                        min: -4,
                                        name: "Relative change of WGBT",
                                        unit: "%"
                                    }}

                                    diverging
                                />
                            </HoverHandler>

                        </div>
                    </Col>
                </Row>

                <Row className="show-grid">
                    <Fade>
                        <Col xs={10} xsOffset={1} sm={8} smOffset={2} md={8} mdOffset={2} lg={6} lgOffset={3}>
                            <p className="ptr-comparator-label centered">Analysis: GISAT © 2019</p>
                        </Col>
                    </Fade>
                </Row>


                <Row className="show-grid">
                    <Fade>
                        <Col xs={12} sm={10} smOffset={1} md={10} mdOffset={1} lg={8} lgOffset={2}>
                            <p className="centered">The second part of the analysis quantifies and compares the heat stress level on the square and in its corridors or specific places for each scenario. You can select an analytical unit in the interactive map window, the relative difference in WBGT index (comparing with current status) for the selected unit is displayed in the chart. With this analytical approach, you can compare the difference for the entire square, for specific functional corridors on the square or even for specific places on the square, where the people are expected to spend more time, so their climate comfort is important.</p>
                            <p className="centered">This analysis shows, that - as it is not possible to preserve the old trees, which are currently on the square - the most favourable scenario, considering the local climate conditions, is obviously the scenario nr. 3, with large crown trees planted all around the square and also with patches of grass larger than in case of all other proposed scenarios.</p>
                            <p className="centered">This case study demonstrates how our modelling method can help to assess the impact of planned modifications of public place design on local climate and heat stress conditions. Through this, it gives the authorities, urban planners, architects and etc. an important instrument to assess the impact of various urban planning scenarios on human thermal comfort and help them to select the most favorable one.</p>
                            <p className="centered">Also, the modelling shows that the urban green elements are important part of all public spaces like squares, living streets etc., while they not only affect the space visually but also have very significant impact on the urban micro-climate environment. </p>
                            <p className="centered">You can also check this analysis in detail in the <a href="https://urban-tep.eu/puma/tool/?id=362732702&lang=en" target="_blank">interactive modelling app</a>.</p>
                        </Col>
                    </Fade>
                </Row>
            </div>
        );
    }

    renderOptions() {
        return options.map(option => {
            return (
                <Option key={option.value} value={option.label}>{option.label}</Option>
            );
        })
    }

    renderScenariosOptions() {
        return scenarioComparatorOptions.map(option => {
            return (
                <Option key={option.value} value={option.label}>{option.label}</Option>
            );
        })
    }
}

Service2.propTypes = {
    id: PropTypes.string,
    name: PropTypes.string,
    textCentered: PropTypes.bool
};

export default Service2;